/**
 * Content Animation Engine powered by Anime.js
 * Supports attributes:
 * - data-animate="in-right|in-left|in-up|in-down|typing|fade-in|zoom-in|zoom-out|blur-in|rotate-in|flip-up|wave|bounce-in|skew-in"
 * - data-split="chars|words|lines|none"
 * - data-delay="0.2" (seconds)
 * - data-animateduration="0.75" (seconds)
 * - data-stagger="0.02" (seconds)
 * - data-ease="power3.out|easeOutExpo|easeOutCubic|linear|..."
 * - data-cursor="true|false"
 * - data-once="false|true" (defaults to false so animation replays on scroll)
 * - data-trigger="scroll|load"
 */

(function () {
  'use strict';

  // Check user preference for reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Map GSAP & web easing names to Anime.js easings
   */
  function mapEasing(ease) {
    if (!ease) return 'easeOutCubic';
    const clean = ease.trim().toLowerCase();
    const map = {
      'power1.out': 'easeOutQuad',
      'power1.in': 'easeInQuad',
      'power1.inout': 'easeInOutQuad',
      'power2.out': 'easeOutCubic',
      'power2.in': 'easeInCubic',
      'power2.inout': 'easeInOutCubic',
      'power3.out': 'easeOutQuart',
      'power3.in': 'easeInQuart',
      'power3.inout': 'easeInOutQuart',
      'power4.out': 'easeOutQuint',
      'power4.in': 'easeInQuint',
      'power4.inout': 'easeInOutQuint',
      'expo.out': 'easeOutExpo',
      'expo.in': 'easeInExpo',
      'expo.inout': 'easeInOutExpo',
      'sine.out': 'easeOutSine',
      'sine.in': 'easeInSine',
      'sine.inout': 'easeInOutSine',
      'back.out': 'easeOutBack',
      'back.in': 'easeInBack',
      'back.inout': 'easeInOutBack',
      'elastic.out': 'easeOutElastic(1, .5)',
      'elastic.in': 'easeInElastic(1, .5)',
      'elastic.inout': 'easeInOutElastic(1, .5)',
      'bounce.out': 'easeOutBounce',
      'bounce.in': 'easeInBounce',
      'bounce.inout': 'easeInOutBounce',
      'linear': 'linear'
    };
    return map[clean] || ease;
  }

  /**
   * Cleanly split text nodes into words and characters.
   * Collapses multiple whitespaces/newlines so unwanted multiple &nbsp; are NEVER created.
   * Preserves HTML tags (strong, em, a, etc.) and ensures natural line wrapping.
   */
  function splitTextNodes(node, splitType) {
    if (node.nodeType === Node.TEXT_NODE) {
      const rawText = node.textContent;

      // Ignore pure whitespace text nodes with newlines (e.g. indentation before/after tags)
      if (!rawText.trim()) {
        return rawText.includes('\n') ? document.createTextNode('') : document.createTextNode(' ');
      }

      // Collapse multiple whitespace/tabs/newlines into a single clean space
      const cleaned = rawText.replace(/\s+/g, ' ');
      const hasLeadingSpace = /^\s/.test(rawText) && !rawText.startsWith('\n');
      const hasTrailingSpace = /\s$/.test(rawText) && !rawText.endsWith('\n');

      const words = cleaned.trim().split(' ');
      const fragment = document.createDocumentFragment();

      if (hasLeadingSpace) {
        const spaceSpan = document.createElement('span');
        spaceSpan.className = 'anime-space';
        spaceSpan.innerHTML = '&nbsp;';
        fragment.appendChild(spaceSpan);
      }

      words.forEach(function (word, index) {
        if (!word) return;

        const wordSpan = document.createElement('span');
        wordSpan.className = 'anime-word';

        if (splitType === 'chars') {
          for (let i = 0; i < word.length; i++) {
            const charSpan = document.createElement('span');
            charSpan.className = 'anime-char';
            charSpan.textContent = word[i];
            wordSpan.appendChild(charSpan);
          }
        } else {
          wordSpan.textContent = word;
        }

        fragment.appendChild(wordSpan);

        // Add exactly ONE single space span between words
        if (index < words.length - 1) {
          const spaceSpan = document.createElement('span');
          spaceSpan.className = 'anime-space';
          spaceSpan.innerHTML = '&nbsp;';
          fragment.appendChild(spaceSpan);
        }
      });

      if (hasTrailingSpace) {
        const spaceSpan = document.createElement('span');
        spaceSpan.className = 'anime-space';
        spaceSpan.innerHTML = '&nbsp;';
        fragment.appendChild(spaceSpan);
      }

      return fragment;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (
        node.classList.contains('anime-cursor') ||
        node.classList.contains('anime-word') ||
        node.classList.contains('anime-char') ||
        node.classList.contains('anime-space')
      ) {
        return node;
      }
      const children = Array.from(node.childNodes);
      children.forEach(function (child) {
        const replacement = splitTextNodes(child, splitType);
        if (replacement !== child) {
          node.replaceChild(replacement, child);
        }
      });
      return node;
    }
    return node;
  }

  /**
   * Set initial CSS transforms / opacity before animation runs
   */
  function prepareInitialState(targets, animType) {
    targets.forEach(function (target) {
      target.style.opacity = '0';
      target.style.willChange = 'transform, opacity';

      switch (animType) {
        case 'in-right':
          target.style.transform = 'translateX(50px)';
          break;
        case 'in-left':
          target.style.transform = 'translateX(-50px)';
          break;
        case 'in-up':
          target.style.transform = 'translateY(35px)';
          break;
        case 'in-down':
          target.style.transform = 'translateY(-35px)';
          break;
        case 'zoom-in':
          target.style.transform = 'scale(0.65)';
          break;
        case 'zoom-out':
          target.style.transform = 'scale(1.3)';
          break;
        case 'blur-in':
          target.style.transform = 'translateY(15px)';
          target.style.filter = 'blur(10px)';
          break;
        case 'rotate-in':
          target.style.transform = 'rotate(-15deg) translateY(25px)';
          break;
        case 'flip-up':
          target.style.transform = 'rotateX(90deg) translateY(20px)';
          if (target.parentElement) {
            target.parentElement.classList.add('anime-perspective');
          }
          break;
        case 'wave':
          target.style.transform = 'translateY(30px)';
          break;
        case 'bounce-in':
          target.style.transform = 'scale(0.3)';
          break;
        case 'skew-in':
          target.style.transform = 'skewX(20deg) translateX(40px)';
          break;
        case 'typing':
          target.style.transform = 'none';
          target.style.filter = 'none';
          break;
        case 'fade-in':
        default:
          target.style.transform = 'none';
          target.style.filter = 'none';
          break;
      }
    });
  }

  /**
   * Initialize and prepare element for animation
   */
  function initElement(el) {
    if (el.dataset.animeInitialized === 'true') return;

    let splitType = (el.getAttribute('data-split') || 'none').toLowerCase();
    const animType = (el.getAttribute('data-animate') || 'fade-in').toLowerCase();

    // Auto-split to chars for typing effect if not specified
    if (animType === 'typing' && splitType === 'none') {
      splitType = 'chars';
      el.setAttribute('data-split', 'chars');
    }

    // Perform text splitting if needed
    if (splitType === 'chars' || splitType === 'words') {
      splitTextNodes(el, splitType);
    }

    // Handle typing cursor if typing mode is active
    if (animType === 'typing') {
      const showCursor = el.getAttribute('data-cursor') !== 'false';
      if (showCursor && !el.querySelector('.anime-cursor')) {
        const cursor = document.createElement('span');
        cursor.className = 'anime-cursor';
        cursor.innerHTML = '|';
        cursor.setAttribute('aria-hidden', 'true');
        el.appendChild(cursor);
      }
    }

    // Query target elements
    let targets = [];
    if (splitType === 'chars') {
      targets = Array.from(el.querySelectorAll('.anime-char'));
    } else if (splitType === 'words') {
      targets = Array.from(el.querySelectorAll('.anime-word'));
    } else if (splitType === 'lines') {
      targets = Array.from(el.querySelectorAll('p, .anime-line'));
      if (targets.length === 0) targets = [el];
    } else {
      targets = [el];
    }

    if (targets.length === 0) targets = [el];

    // Store targets on element for trigger
    el._animeTargets = targets;
    el.dataset.animeInitialized = 'true';

    if (prefersReducedMotion) {
      targets.forEach(function (t) {
        t.style.opacity = '1';
        t.style.transform = 'none';
        t.style.filter = 'none';
      });
      return;
    }

    // Set initial pre-animation state
    prepareInitialState(targets, animType);
  }

  /**
   * Run Anime.js animation on element
   */
  function playAnimation(el) {
    if (el.dataset.animePlaying === 'true') return;
    const isOnce = el.getAttribute('data-once') === 'true';
    if (el.dataset.animeDone === 'true' && isOnce) return;

    if (prefersReducedMotion) {
      if (el._animeTargets) {
        el._animeTargets.forEach(function (t) {
          t.style.opacity = '1';
          t.style.transform = 'none';
          t.style.filter = 'none';
        });
      }
      el.dataset.animeDone = 'true';
      return;
    }

    if (typeof window.anime === 'undefined') {
      // Anime.js not yet ready, show immediately as fallback
      if (el._animeTargets) {
        el._animeTargets.forEach(function (t) {
          t.style.opacity = '1';
          t.style.transform = 'none';
          t.style.filter = 'none';
        });
      }
      return;
    }

    const targets = el._animeTargets || [el];
    const animType = (el.getAttribute('data-animate') || 'fade-in').toLowerCase();
    const delay = (parseFloat(el.getAttribute('data-delay')) || 0) * 1000;
    const duration = (parseFloat(el.getAttribute('data-animateduration')) || 0.75) * 1000;
    const stagger = (parseFloat(el.getAttribute('data-stagger')) || 0.02) * 1000;
    const ease = mapEasing(el.getAttribute('data-ease') || 'power3.out');

    // Remove any previous ongoing animation on these targets
    anime.remove(targets);

    el.dataset.animePlaying = 'true';

    // Animation configuration builder
    const config = {
      targets: targets,
      delay: anime.stagger(stagger, { start: delay }),
      easing: ease,
      begin: function () {
        el.classList.add('is-animating');
      },
      complete: function () {
        el.dataset.animePlaying = 'false';
        el.dataset.animeDone = 'true';
        el.classList.remove('is-animating');
        el.classList.add('is-animated');
      }
    };

    switch (animType) {
      case 'typing':
        config.opacity = [0, 1];
        config.duration = Math.max(25, duration / targets.length);
        config.easing = 'linear';
        config.delay = anime.stagger(stagger > 0 ? stagger : 35, { start: delay });
        config.complete = function () {
          el.dataset.animePlaying = 'false';
          el.dataset.animeDone = 'true';
          el.classList.add('is-animated');
          const cursor = el.querySelector('.anime-cursor');
          if (cursor && el.getAttribute('data-cursor-hide') === 'true') {
            setTimeout(function () {
              cursor.classList.add('is-hidden');
            }, 3000);
          }
        };
        break;

      case 'in-right':
        config.translateX = [50, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'in-left':
        config.translateX = [-50, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'in-up':
        config.translateY = [35, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'in-down':
        config.translateY = [-35, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'zoom-in':
        config.scale = [0.65, 1];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'zoom-out':
        config.scale = [1.3, 1];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'blur-in':
        config.filter = ['blur(10px)', 'blur(0px)'];
        config.translateY = [15, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'rotate-in':
        config.rotate = [-15, 0];
        config.translateY = [25, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'flip-up':
        config.rotateX = [90, 0];
        config.translateY = [20, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'wave':
        config.translateY = [30, -8, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        config.easing = 'easeOutQuad';
        break;

      case 'bounce-in':
        config.scale = [0.3, 1];
        config.opacity = [0, 1];
        config.duration = duration * 1.3;
        config.easing = 'easeOutElastic(1, .6)';
        break;

      case 'skew-in':
        config.skewX = [20, 0];
        config.translateX = [40, 0];
        config.opacity = [0, 1];
        config.duration = duration;
        break;

      case 'fade-in':
      default:
        config.opacity = [0, 1];
        config.duration = duration;
        break;
    }

    anime(config);
  }

  /**
   * Observe elements using IntersectionObserver.
   * By default data-once is false (unless specified as true), so animation replays
   * when scrolling down and scrolling back up!
   */
  function setupScrollObserver() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    if (animatedElements.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      animatedElements.forEach(function (el) {
        initElement(el);
        playAnimation(el);
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const el = entry.target;
          const isOnce = el.getAttribute('data-once') === 'true';

          if (entry.isIntersecting) {
            initElement(el);
            playAnimation(el);

            if (isOnce) {
              observer.unobserve(el);
            }
          } else {
            // When leaving viewport, reset state so it replays smoothly when scrolling back!
            if (!isOnce && (el.dataset.animeDone === 'true' || el.dataset.animePlaying === 'true')) {
              if (el._animeTargets && window.anime) {
                anime.remove(el._animeTargets);
              }
              el.dataset.animeDone = 'false';
              el.dataset.animePlaying = 'false';
              el.classList.remove('is-animating', 'is-animated');

              const animType = (el.getAttribute('data-animate') || 'fade-in').toLowerCase();
              if (el._animeTargets) {
                prepareInitialState(el._animeTargets, animType);
              }
              const cursor = el.querySelector('.anime-cursor');
              if (cursor) {
                cursor.classList.remove('is-hidden');
              }
            }
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    animatedElements.forEach(function (el) {
      if (el.getAttribute('data-trigger') === 'load') {
        initElement(el);
        playAnimation(el);
      } else {
        initElement(el);
        observer.observe(el);
      }
    });
  }

  /**
   * Ensure Anime.js is loaded then run initialization
   */
  function run() {
    if (typeof window.anime !== 'undefined') {
      setupScrollObserver();
    } else {
      let attempts = 0;
      const interval = setInterval(function () {
        attempts++;
        if (typeof window.anime !== 'undefined') {
          clearInterval(interval);
          setupScrollObserver();
        } else if (attempts > 60) {
          // Timeout: show text safely to avoid blank content
          clearInterval(interval);
          document.querySelectorAll('[data-animate]').forEach(function (el) {
            el.style.opacity = '1';
            el.style.transform = 'none';
          });
        }
      }, 50);
    }
  }

  // Safety fallback after 3.5s to ensure no text is left invisible
  setTimeout(function () {
    document.querySelectorAll('[data-animate]').forEach(function (el) {
      if (!el.classList.contains('is-animated') && !el.classList.contains('is-animating')) {
        if (el._animeTargets) {
          el._animeTargets.forEach(function (t) {
            t.style.opacity = '1';
            t.style.transform = 'none';
            t.style.filter = 'none';
          });
        } else {
          el.style.opacity = '1';
          el.style.transform = 'none';
        }
      }
    });
  }, 3500);

  // Initialize on DOMContentLoaded and Load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Shopify Theme Customizer Live Preview Events
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-animate]')) {
      run();
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    const animatedElements = event.target.querySelectorAll('[data-animate]');
    animatedElements.forEach(function (el) {
      el.dataset.animeDone = 'false';
      el.dataset.animePlaying = 'false';
      initElement(el);
      playAnimation(el);
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    const el = event.target.hasAttribute('data-animate')
      ? event.target
      : event.target.querySelector('[data-animate]');
    if (el) {
      el.dataset.animeDone = 'false';
      el.dataset.animePlaying = 'false';
      initElement(el);
      playAnimation(el);
    }
  });
})();
